import React, { useState } from "react";
import Fallback from "../../assets/images/empresas/fallback.jpg";
import { useTheme } from "../../contexts/Theme/ThemeContext";
import { ImagemZoom } from "../../pages/Pedidos/Pedido/styles";
import { TIPO_MIDIA } from "../../services/Consts";
import { obterMiniaturaVideoYouTube } from "../../services/Functions";
import { ProdutoTable } from "../../types/Produto";
import { LayoutTabelaProdutos, defaultLayoutTabelaProdutos } from "../../types/LayoutImpressao";
import { ReactComponent as Atencao } from "../../assets/images/uteis/atencao.svg";
import ImgWithLoading from "../ImgWithLoading/ImgWithLoading";
import Modal from "../Modal/Modal";
import "./styles";
import { TableProdutosContainer } from "./styles";
import OrdenacaoButton from "./OrdenacaoButton/OrdenacaoButton";
import {
  OrdenacaoConfig,
  carregarOrdenacao,
  ordenarProdutos,
  ordenarVariacoes,
} from "./OrdenacaoButton/ordenacaoUtils";

interface TableProdutosProps {
  produtos: ProdutoTable[];
  moeda?: string;
  template: "principal" | "impressao";
  sortVariacoes?: boolean;
  layoutConfig?: LayoutTabelaProdutos;
  ordenacaoExterna?: OrdenacaoConfig;
}

const TableProdutos: React.FC<TableProdutosProps> = ({
  produtos,
  template,
  moeda = "R$",
  sortVariacoes = false,
  layoutConfig = defaultLayoutTabelaProdutos,
  ordenacaoExterna,
}) => {
  const config = layoutConfig;
  const [modalProdutoFoto, setModalProdutoFoto] = useState(false);
  const [imgSrcZoom, setImgSrcZoom] = useState("");
  const [ordenacao, setOrdenacao] = useState<OrdenacaoConfig>(carregarOrdenacao);

  // Usar ordenacaoExterna se passada, senão usar estado interno
  const ordenacaoEfetiva = ordenacaoExterna ?? ordenacao;

  const formatarPeso = (peso: number) => {
    const valor = Math.round(peso * 10000) / 10000;
    return valor.toLocaleString("pt-BR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4,
    });
  };

  // Aplicar ordenação aos produtos
  const produtosOrdenados = ordenarProdutos(produtos, ordenacaoEfetiva.produto);

  const temSwatchNaTabela = produtos.some((p) =>
    (p.variacoes || []).some((v) => !!(v.variacao_imagem || v.variacao_cor_hex))
  );

  type SwatchStyle = React.CSSProperties & { "--swatch-cor"?: string };
  const estiloSwatchCor = (cor?: string | null): SwatchStyle => ({ "--swatch-cor": cor || "transparent" });

  // Calcular número de colunas visíveis para o grid de variações
  const colunasVariacao = [
    config.produtoVariacao,
    config.produtoPeso,
    config.produtoQuantidade,
    config.produtoPreco,
    config.produtoTotal,
  ].filter(Boolean).length;

  // Verificar se a coluna de produto deve ser exibida
  const temColunaProduto = config.produtoImagem || config.produtoReferencia ||
    config.produtoNome || config.produtoDescricao;

  // Há algum campo de variação configurado para impressão?
  // (colunas de variação ou a observação da variação). Quando nenhum estiver
  // ativo, não renderizamos o bloco de observação nem suas linhas separadoras.
  const temCamposVariacao = colunasVariacao > 0 || config.produtoObservacao;

  const { theme } = useTheme();

  return (
    <>
      {template === "principal" && (
        <OrdenacaoButton
          ordenacao={ordenacao}
          onOrdenacaoChange={setOrdenacao}
        />
      )}
      <TableProdutosContainer
        className={`${
          template === "principal" ? "scrollBonitoBottom" : ""
        } table-produtos`}
        theme={theme}
        colunasVariacao={colunasVariacao}
        template={template}
        temImagem={config.produtoImagem}
        temColunaProduto={temColunaProduto}
      >
        <table className="table-grid">
          <thead>
            <tr className="row-simulated fonteTexto">
              {temColunaProduto && <th className="produtos">Produto</th>}
              {config.produtoVariacao && <th className="variacao">Variação</th>}
              {config.produtoPeso && <th className="peso">Peso</th>}
              {config.produtoQuantidade && <th className="qtd">{template === "principal" ? "Quantidade" : "Qtd"}</th>}
              {config.produtoPreco && <th className="preco">Preço</th>}
              {config.produtoTotal && <th className="total">Total</th>}
            </tr>
          </thead>
          <tbody>
            {produtosOrdenados.map((produto, index) => {
              // Aplicar ordenação de variações (do estado ou da prop sortVariacoes para compatibilidade)
              const sortedVariacoes = ordenacaoEfetiva.variacao
                ? ordenarVariacoes(produto.variacoes || [], ordenacaoEfetiva.variacao)
                : sortVariacoes
                ? [...(produto.variacoes || [])].sort((a, b) =>
                    (a.variacao ?? "").localeCompare(b.variacao ?? "")
                  )
                : produto.variacoes || [];
              return (
                <React.Fragment key={produto.id_produto}>
                  <tr className="row-simulated">
                    {temColunaProduto && (
                      <div
                        className="flex-produto"
                        style={{
                          gridRowStart: `span ${
                            (produto.variacoes?.length || 0) * 2
                          }`,
                        }}
                      >
                        {config.produtoImagem && (
                          <div className="produto-imagem">
                            <div
                              className="imagem-produto-container"
                              onClick={() => {
                                setModalProdutoFoto(true);
                                setImgSrcZoom(
                                  Number.parseInt(produto.tipo_midia!) ===
                                    TIPO_MIDIA.YOUTUBE
                                    ? obterMiniaturaVideoYouTube(produto.imagem)
                                    : produto.imagem || Fallback
                                );
                              }}
                            >
                              <ImgWithLoading
                                src={
                                  Number.parseInt(produto.tipo_midia!) ===
                                  TIPO_MIDIA.YOUTUBE
                                    ? obterMiniaturaVideoYouTube(produto.imagem)
                                    : produto.imagem || Fallback
                                }
                                alt=""
                              />
                            </div>
                          </div>
                        )}
                        <div className="informacoes-produto fonteTexto">
                          {produto.is_brinde && template === "principal" && (
                            <div>
                              <span
                                className="fonteRotulos"
                                style={{
                                  display: "inline-block",
                                  background: theme.colors.primary,
                                  color: theme.colors.white,
                                  padding: "2px 13px",
                                  borderRadius: "3rem",
                                }}
                              >
                                Brinde
                              </span>
                            </div>
                          )}
                          {config.produtoReferencia && (
                            <div className="fonteTexto">
                              {produto.referencia}
                            </div>
                          )}
                          {produto.ean && template === "impressao" && (
                            <div className="ean fonteRotulos">EAN: {produto.ean}</div>
                          )}
                          {config.produtoNome && (
                            <div>
                              <b>{produto.nome}</b>
                            </div>
                          )}
                          {config.produtoDescricao && produto.descricao && (
                            <div
                              className="descricao"
                              dangerouslySetInnerHTML={{
                                __html: produto.descricao || "",
                              }}
                            />
                          )}
                        </div>
                      </div>
                    )}
                    {sortedVariacoes.map((variacao, index) => {
                      const temSwatch = !!(variacao.variacao_imagem || variacao.variacao_cor_hex);
                      return (
                        <React.Fragment key={variacao.id_variacao}>
                          {config.produtoVariacao && (
                            <div className={`flex-variacoes align-left fonteTexto${temSwatchNaTabela ? " com-swatch" : ""}`}>
                              {temSwatchNaTabela ? (
                                <span className="variacao-linha">
                                  {temSwatch && (
                                    variacao.variacao_imagem ? (
                                      <img
                                        className="variacao-swatch"
                                        src={variacao.variacao_imagem}
                                        alt={variacao.variacao || ""}
                                      />
                                    ) : (
                                      <span
                                        className="variacao-swatch"
                                        role="img"
                                        aria-label={variacao.variacao || ""}
                                        style={estiloSwatchCor(variacao.variacao_cor_hex)}
                                      />
                                    )
                                  )}
                                  <span className="variacao-texto">
                                    {variacao.sku && <div className="fonteRotulos">{variacao.sku}</div>}
                                    {variacao.variacao}
                                  </span>
                                </span>
                              ) : (
                                <span className="align-left">
                                  {variacao.sku && <div className="fonteRotulos">{variacao.sku}</div>}
                                  {variacao.variacao}
                                </span>
                              )}
                            </div>
                          )}
                          {config.produtoPeso && (
                            <div className="flex-variacoes fonteTexto">
                              <span>
                                {variacao.peso != null ? `${formatarPeso(variacao.peso)}g` : (produto.peso ? `${formatarPeso(produto.peso)}g` : "-")}
                              </span>
                            </div>
                          )}
                          {config.produtoQuantidade && (
                            <div className="flex-variacoes fonteTexto">
                              <span>
                                <div
                                  style={{
                                    marginLeft: `${
                                      variacao.estoque === 0 ? "2.4rem" : "0"
                                    }`,
                                  }}
                                >
                                  {variacao.quantidade}
                                </div>{" "}
                                {variacao.estoque === 0 ? <Atencao /> : null}
                              </span>
                            </div>
                          )}
                          {config.produtoPreco && (
                            <div className="flex-variacoes  fonteTexto">
                              <span className="preco-container">
                                {variacao.preco_original != null &&
                                  variacao.preco_original > 0 &&
                                  variacao.preco_original > variacao.preco && (
                                    <span className="preco-original">
                                      {`${moeda} ${Math.round(variacao.preco_original * 100)
                                        .toString()
                                        .padStart(3, "0")
                                        .replace(/(\d+)(\d{2})$/, "$1,$2")}`}
                                    </span>
                                  )}
                                {produto.is_brinde
                                  ? "Grátis"
                                  : variacao.preco === 0 ||
                                variacao.preco === undefined
                                  ? template === "principal"
                                    ? "A combinar"
                                    : "-"
                                  : `${moeda} ${Math.round(variacao.preco * 100)
                                      .toString()
                                      .padStart(3, "0")
                                      .replace(/(\d+)(\d{2})$/, "$1,$2")}`}
                              </span>
                            </div>
                          )}
                          {config.produtoTotal && (
                            <div className="flex-variacoes  fonteTexto">
                              <span>
                                {produto.is_brinde
                                  ? "Grátis"
                                  : variacao.total === 0 ||
                                variacao.total === undefined
                                  ? template === "principal"
                                    ? "A combinar"
                                    : "-"
                                  : `${moeda} ${Math.round(variacao.total * 100)
                                      .toString()
                                      .padStart(3, "0")
                                      .replace(/(\d+)(\d{2})$/, "$1,$2")}`}
                              </span>
                            </div>
                          )}
                          {temCamposVariacao && (
                            <div className="observacao">
                              {index !== sortedVariacoes.length - 1 && (
                                <div className="separator-line-obs" />
                              )}
                              {config.produtoObservacao && variacao.observacao && (
                                <span>
                                  <>
                                    <b>Observação: </b>
                                    {variacao.observacao || ""}
                                  </>
                                </span>
                              )}
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                  <div className="separator-line" />
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={(temColunaProduto ? 1 : 0) + colunasVariacao}>
                <div className="flex-footer fonteRotulos">
                  {config.footerUltimaPeca &&
                    produtosOrdenados.reduce((acc, produto) => {
                      return (
                        acc +
                        produto.variacoes!.reduce((acc, variacao) => {
                          if (variacao.estoque === 0) {
                            return acc + 1;
                          }
                          return acc;
                        }, 0)
                      );
                    }, 0) > 0 && (
                      <div className="info">
                        <Atencao /> Últimos produtos do estoque:{" "}
                        {produtosOrdenados.reduce((acc, produto) => {
                          return (
                            acc +
                            produto.variacoes!.reduce((acc, variacao) => {
                              if (variacao.estoque === 0) {
                                return acc + 1;
                              }
                              return acc;
                            }, 0)
                          );
                        }, 0)}
                      </div>
                    )}
                  {config.footerPesoTotal && (
                    <div className="info">
                      {produtosOrdenados.reduce((acc, produto) => {
                        return (
                          acc +
                          produto.variacoes!.reduce((acc, variacao) => {
                            const pesoVar = variacao.peso ?? produto.peso ?? 0;
                            return acc + pesoVar * variacao.quantidade;
                          }, 0)
                        );
                      }, 0) > 0 && (
                        <>
                          Peso total:{" "}
                          {`${formatarPeso(produtosOrdenados.reduce((acc, produto) => {
                            return (
                              acc +
                              produto.variacoes!.reduce((acc, variacao) => {
                                const pesoVar = variacao.peso ?? produto.peso ?? 0;
                                return acc + pesoVar * variacao.quantidade;
                              }, 0)
                            );
                          }, 0))}g`}
                        </>
                      )}
                    </div>
                  )}
                  {config.footerTotalProdutos && (
                    <div className="info">
                      Total de produtos:{" "}
                      {produtosOrdenados.reduce((acc, produto) => {
                        return (
                          acc +
                          produto.variacoes!.reduce((acc, variacao) => {
                            return acc + variacao.quantidade;
                          }, 0)
                        );
                      }, 0)}
                    </div>
                  )}
                  {config.footerValorProdutos && (
                    <div className="info">
                      Valor dos produtos:{" "}
                      {`${moeda} ${produtosOrdenados
                        .reduce((acc, produto) => {
                          return (
                            acc +
                            produto.variacoes!.reduce((acc, variacao) => {
                              return acc + (variacao.total || 0);
                            }, 0)
                          );
                        }, 0)
                        .toFixed(2)
                        .replace(".", ",")}`}
                    </div>
                  )}
                </div>
              </td>
            </tr>
          </tfoot>
        </table>
      </TableProdutosContainer>
      <Modal
        isOpen={modalProdutoFoto}
        setModal={setModalProdutoFoto}
        width="100rem"
        maxWidth="99vw"
        align="center"
        justify="center"
        animation="opacity 500ms cubic-bezier(0.4, 0, 0.2, 1)"
        margin="0rem 2rem 0rem 2rem"
      >
        <ImagemZoom onClick={() => setModalProdutoFoto(false)}>
          <ImgWithLoading fullSize={true} src={imgSrcZoom} alt="" />
        </ImagemZoom>
      </Modal>
    </>
  );
};

export default TableProdutos;
